import { useMemo } from "react";

import { SortOption } from "components/Filters/types";
import useMakeFiltersData from "components/Filters/useMakeFiltersData";

import { SEARCH_MANAGED_ENTITIES_SUGGESTIONS_DICTIONARY } from "./constants";
import { IacManagementGroupBy } from "../types";
import { getFilterKey } from "./helpers";
import useIacManagementSuggestions from "./useSuggestionsQuery";

type UseIacManagementFiltersProps = {
  parentStackId?: string;
  groupBy: IacManagementGroupBy;
};

const useIacManagementFilters = ({ parentStackId, groupBy }: UseIacManagementFiltersProps) => {
  const { cachedFiltersData, loading, refetch, allResourcesQty } = useIacManagementSuggestions({
    parentStackId,
  });

  const { filters, filterOptionsMap } = useMakeFiltersData({
    suggestions: cachedFiltersData,
    dictionary: SEARCH_MANAGED_ENTITIES_SUGGESTIONS_DICTIONARY,
  });

  const stackItemsCount = useMemo(() => {
    switch (groupBy) {
      case IacManagementGroupBy.Provider:
        return (
          cachedFiltersData.find((item) => item.name === "provider")?.possibleValuesString?.values
            .length || 0
        );
      case IacManagementGroupBy.Type:
        return (
          cachedFiltersData.find((item) => item.name === "type")?.possibleValuesString?.values
            .length || 0
        );
      case IacManagementGroupBy.None:
      case IacManagementGroupBy.Stack:
      default:
        return (
          cachedFiltersData.find((item) => item.name === "stack")?.possibleValuesString?.values
            .length || 0
        );
    }
  }, [cachedFiltersData, groupBy]);

  const sortOptions = useMemo((): SortOption[] => {
    return (
      cachedFiltersData
        .filter((field) => field.orderable)
        .map((field) => {
          return {
            value: field.name,
            label: getFilterKey(field.name),
          };
        }) || []
    );
  }, [cachedFiltersData]);

  return {
    filters,
    filtersMap: filterOptionsMap,
    refetch,
    loading,
    itemsCount: stackItemsCount,
    sortOptions,
    allResourcesQty,
  };
};

export default useIacManagementFilters;
